import { AccessToken, GetAccessTokenResponse } from '../models';
import { DynamicState, OrderMember, SelectorUtil, User } from '@us/web/shared/data-access';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { addSeconds, isAfter } from 'date-fns';
import { cloneDeep } from 'lodash';
import { AuthActions } from './auth.actions';
import { getBoolean, uniqueId } from '@us/web/shared/utils';

export const AUTH_FEATURE_KEY = 'auth';

export interface State extends DynamicState<'user', User | null> {
    token: AccessToken | null;
    apiDomain: string | null;
    sessionUser: {
        id: string;
        name: string;
    };
}

export const initialState = (): State => ({
    token: null,
    user: null,
    sessionUser: {
        id: uniqueId(),
        name: ''
    },
    apiDomain: null,
    status: 'pending',
    error: null
});

const onGetTokenSuccess = (state: State, data: GetAccessTokenResponse): State => {
    const { access_token: accessToken, token_type: tokenType, expires_in: expiresIn, api_domain: apiDomain } = data;
    const expiresAt: Date = addSeconds(new Date(), Number(expiresIn));

    const token: AccessToken = {
        accessToken,
        tokenType,
        expiresIn,
        expiresAt: expiresAt.toJSON()
    };

    return { ...state, token, apiDomain, status: 'success' };
};

const onLoginSuccess = (state: State, user: User, tokenServer: GetAccessTokenResponse): State => {
    const { access_token: accessToken, token_type: tokenType, expires_in: expiresIn } = tokenServer;
    const expiresAt: Date = addSeconds(new Date(), Number(expiresIn));

    const token: AccessToken = {
        accessToken,
        tokenType,
        expiresIn,
        expiresAt: expiresAt.toJSON()
    };

    return {
        ...state,
        token,
        user: user,
        sessionUser: {
            id: user.id.toString(),
            name: user.name
        },
        status: 'success',
        apiDomain: tokenServer.api_domain
    };
};

export const onLogout = () => cloneDeep(initialState());

export const authFeature = createFeature({
    name: AUTH_FEATURE_KEY,
    reducer: createReducer(
        initialState(),
        on(
            AuthActions.getToken,
            AuthActions.login,
            AuthActions.updateAccountProfile,
            (state: State): State => ({
                ...state,
                status: 'loading'
            })
        ),
        on(AuthActions.getTokenSuccess, (state: State, { data }) => onGetTokenSuccess(state, data)),
        on(AuthActions.loginSuccess, AuthActions.updateAccountProfileSuccess, (state: State, { user, token }) =>
            onLoginSuccess(state, user, token)
        ),
        on(
            AuthActions.loginFailure,
            AuthActions.updateAccountProfileFailure,
            (state: State, { error }): State => ({
                ...state,
                status: 'error',
                error
            })
        ),
        on(AuthActions.logoutConfirmed, () => onLogout()),
        on(AuthActions.updateSessionUserName, (state, { name }) => ({
            ...state,
            sessionUser: {
                ...state.sessionUser,
                name
            }
        }))
    )
});

export const { name, reducer, selectAuthState, selectStatus, selectToken, selectUser, selectSessionUser } = authFeature;

export const selectIsLoading = createSelector(selectAuthState, SelectorUtil.isLoading);

const isTokenValid = (token: AccessToken | null) =>
    !!token && !!token.accessToken && !!token.expiresAt && isAfter(new Date(token.expiresAt), new Date());

export const selectIsTokenValid = createSelector(selectAuthState, state => isTokenValid(state.token));

export const selectValidatedToken = createSelector(selectAuthState, state =>
    isTokenValid(state.token) ? state.token : null
);

export const selectApiDomain = createSelector(selectAuthState, state => state.apiDomain);

export const selectUserId = createSelector(selectUser, user => user?.id);
export const selectIsLoggedIn = createSelector(selectUser, user => user !== null);
export const selectIsNotLoggedIn = createSelector(selectUser, user => user === null);

export const selectUserPhoneNumber = createSelector(selectUser, user => user?.phone || null);
export const selectUserHasPassword = createSelector(selectUser, user => user?.pin_code_exist || false);
export const selectResetPasswordFlg = createSelector(selectUser, user => getBoolean(user?.reset_password_flg));

export const selectNeedToUpdatePassword = createSelector(
    selectUser,
    user => !user?.pin_code_exist || getBoolean(user?.reset_password_flg)
);

export const selectSessionUserId = createSelector(selectSessionUser, user => user?.id);

export const selectPersistedUserId = createSelector(
    selectUserId,
    selectSessionUserId,
    (userId, sessionUserId) => userId?.toString() || sessionUserId
);
